import { getAbbreviation, getIndicatorHtml, formatNumberLocally } from '../utils/formatters.js';
import { exportSlotToCSV, exportSlotToDiscord } from './export.js';
import { generateShareLink } from './share.js';
import { evaluateEquation } from './toggles.js';

const implicitStatClassMap = {
    "Cavalry Attack in the Arena": "Cavalry",
    "Ranged Attack in the Arena": "Ranged",
    "Infantry Attack in the Arena": "Infantry"
};

const NON_TROOP_CLASSES = ['dragon', 'creature', 'sop', 'troop', 'utility', 'none'];

const getStatTargetClass = (baseKey, fallbackClass) => {
    if (implicitStatClassMap[baseKey]) {
        return implicitStatClassMap[baseKey];
    }
    if (fallbackClass && !NON_TROOP_CLASSES.includes(fallbackClass.toLowerCase())) {
        return fallbackClass;
    }
    return null;
};

const classSpecificStats = [
    "Attack", "Dragon Attack", "Defense", "Dragon Defense", "Health", "Dragon Health",
    "Attack vs Player", "Dragon Attack vs Player", "Defense vs Player", "Dragon Defense vs Player",
    "Health vs Player", "Dragon Health vs Player",
    "Attack vs Player at SOP", "Dragon Attack vs Player at SOP",
    "Defense vs Player at SOP", "Dragon Defense vs Player at SOP",
    "Health vs Player at SOP", "Dragon Health vs Player at SOP",
    "Attack vs SOP", "Dragon Attack vs SOP",
    "Defense vs SOP", "Dragon Defense vs SOP",
    "Health vs SOP", "Dragon Health vs SOP",
    "Attack While Marching vs Player", "Dragon Marcher Attack vs Player", "Defense While Marching vs Player", "Dragon Marcher Defense vs Player",
    "Health While Marching vs Player", "Dragon Marcher Health vs Player",
    "Attack vs Infantry", "Attack vs Ranged", "Attack vs Cavalry",
    "Defense vs Infantry", "Defense vs Ranged", "Defense vs Cavalry",
    "Cavalry Attack in the Arena", "Ranged Attack in the Arena", "Infantry Attack in the Arena"
];

/**
 * Handles the display of comparison results in the UI.
 */
export function displayResults(data, set1Input, set2Input) {
    const container = document.getElementById('results');
    const content = document.getElementById('resultsContent');

    const prevTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';

    content.innerHTML = '';
    container.classList.remove('hidden');

    const exportActions = document.createElement('div');
    exportActions.className = 'export-actions';

    const inputWrapper = document.createElement('div');
    inputWrapper.className = 'export-input-wrapper';

    const inputTitle = document.createElement('h3');
    inputTitle.className = 'export-title';
    inputTitle.textContent = 'Name';

    const exportActionsSection = document.createElement('div');
    exportActionsSection.className = 'export-actions-section';

    const exportButtons = document.createElement('div');
    exportButtons.className = 'export-buttons';

    const btnShare = document.createElement('button');
    btnShare.type = 'button';
    btnShare.className = 'export-btn share-btn';
    btnShare.innerHTML = `
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
        </svg>
        Copy Link
    `;
    btnShare.title = 'Copy a link to share these exact gear selections';
    btnShare.onclick = () => generateShareLink();

    exportButtons.appendChild(btnShare);

    const titleInput = document.createElement('input');
    titleInput.type = 'text';
    titleInput.id = 'comparisonTitle';
    titleInput.className = 'comparison-title-input';
    titleInput.placeholder = 'Enter name...';
    titleInput.value = prevTitle;
    titleInput.maxLength = 60;

    const errorSpan = document.createElement('span');
    errorSpan.className = 'export-error-msg';
    errorSpan.textContent = 'Maximum 60 characters reached.';

    titleInput.addEventListener('input', (e) => {
        window._savedComparisonTitle = e.target.value;
        if (e.target.value.length >= 60) {
            errorSpan.style.display = 'block';
            titleInput.style.borderColor = 'var(--negative-color, #f44336)';
        } else {
            errorSpan.style.display = 'none';
            titleInput.style.borderColor = 'var(--border-color, #ccc)';
        }
    });

    // Check initial state
    if (prevTitle.length >= 60) {
        errorSpan.style.display = 'block';
        titleInput.style.borderColor = 'var(--negative-color, #f44336)';
    }

    inputWrapper.appendChild(inputTitle);
    inputWrapper.appendChild(titleInput);
    exportActionsSection.appendChild(exportButtons);
    exportActions.appendChild(inputWrapper);
    exportActions.appendChild(exportActionsSection);
    content.appendChild(exportActions);

    const hasStats = (sc) => {
        if (!sc || !sc.set1 || !sc.set2) return false;
        const m1 = sc.set1.meta || {};
        const m2 = sc.set2.meta || {};
        const hasStacked = Object.keys(sc.set1.stacked || {}).length > 0 ||
            Object.keys(sc.set2.stacked || {}).length > 0 ||
            Object.keys(sc.set1.health || {}).length > 0 ||
            Object.keys(sc.set2.health || {}).length > 0 ||
            Object.keys(sc.set1.multipliers || {}).length > 0 ||
            Object.keys(sc.set2.multipliers || {}).length > 0;
        return (m1.total_base > 0 || m2.total_base > 0 ||
            m1.total_defense > 0 || m2.total_defense > 0 ||
            m1.total_health > 0 || m2.total_health > 0 ||
            m1.total_dh > 0 || m2.total_dh > 0 || hasStacked);
    };

    // Calculate totals across all selected slots
    const slots = ['helmet', 'chest', 'pants', 'weapon', 'ring', 'boots', 'trinket1', 'trinket2', 'saddle', 'chanfron', 'peytral'];
    let comparedSlotsCount = 0;
    const totals = {
        attack_sop: null, defense_sop: null, dragon: null, march_size: null, sop: null, creatures: null, balanced_sop: null, all_stats: null
    };

    slots.forEach(slot => {
        const slotData = data[slot];
        if (!slotData) return;

        const scenarios = ['attack_sop', 'defense_sop', 'dragon', 'march_size', 'sop', 'creatures', 'balanced_sop', 'all_stats'];
        const slotHasStats = scenarios.some(s => hasStats(slotData[s]));

        if (slotHasStats) {
            comparedSlotsCount++;
            scenarios.forEach(s => {
                totals[s] = mergeScenarioStats(totals[s], slotData[s], slotData.class1, slotData.class2);
            });
        }
    });

    if (comparedSlotsCount === 0) {
        const noResultsMsg = document.createElement('div');
        noResultsMsg.className = 'no-results-msg';
        noResultsMsg.style.textAlign = 'center';
        noResultsMsg.style.padding = '3rem 1rem';
        noResultsMsg.style.color = 'var(--text-color, #333)';
        
        noResultsMsg.innerHTML = `
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom: 1rem; opacity: 0.5;">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <h3 style="margin-bottom: 0.5rem; color: var(--header-text, #fff);">No Relevant Stats Found</h3>
            <p style="opacity: 0.8; max-width: 400px; margin: 0 auto;">
                The selected gear pieces don't have any stats that apply to the selected scenarios. Try selecting a different combat scenario or comparing different gear.
            </p>
        `;
        content.appendChild(noResultsMsg);
        container.scrollIntoView({ behavior: 'smooth' });
        return;
    }

    // March Size Projection Summary Card
    const marchSizeScenarioChecked = document.getElementById('toggle_march_size')?.checked || false;
    const marchSizeData = totals?.march_size;
    const compactContainer = document.getElementById('compact_march_container');
    let isMarchCalculated = false;

    if (marchSizeScenarioChecked && marchSizeData && marchSizeData.set1 && marchSizeData.set2) {
        const rawMs = document.getElementById('unequipped_march_size')?.value || '';
        let msVal = evaluateEquation(rawMs);
        if (isNaN(msVal) || msVal <= 0) {
            msVal = parseFloat(rawMs || '0');
        }
        if (msVal > 1000000) {
            msVal = 1000000;
        }
        const waifChecked = document.getElementById('percent_toggle_waif')?.checked || false;
        const keepEnhChecked = document.getElementById('percent_toggle_keep_enh')?.checked || false;
        const mpVal = (waifChecked ? 6.56 : 0) + (keepEnhChecked ? 8.00 : 0);
        const includeDragon = document.getElementById('march_include_dragon')?.checked || false;

        if (msVal > 0) {
            isMarchCalculated = true;

            // Parse terms to apply active percent scaling only to the first term
            let firstTerm = 0;
            let additionalFlat = 0;
            let s = rawMs.trim();
            if (s.endsWith('+')) {
                s = s.slice(0, -1);
            }
            if (s && /^[0-9]+(\+[0-9]+)*$/.test(s)) {
                const parts = s.split('+').map(p => parseInt(p, 10));
                firstTerm = parts[0] || 0;
                additionalFlat = parts.slice(1).reduce((sum, val) => sum + val, 0);
            } else {
                firstTerm = parseFloat(rawMs || '0');
                if (isNaN(firstTerm) || firstTerm <= 0) {
                    firstTerm = 0;
                }
            }

            const unequippedFlat = (firstTerm / (1 + mpVal / 100)) + additionalFlat;
            const fmt = (num) => Math.round(num).toLocaleString('en-US');
            
            let derivationLabel = `entered march size ${fmt(msVal)} and percent bonuses ${mpVal}%`;
            if (additionalFlat > 0) {
                derivationLabel = `first term ${fmt(firstTerm)} scaled by percent bonuses ${mpVal}%, plus flat addition ${fmt(additionalFlat)}`;
            }

            const fmtPct = (num) => num.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + '%';

            const scenariosList = [];
            const keepChecked = document.getElementById('march_scenario_keep')?.checked || false;
            const playerSopChecked = document.getElementById('march_scenario_player_sop')?.checked || false;
            const reinSopChecked = document.getElementById('march_scenario_rein_sop')?.checked || false;
            const creaturesChecked = document.getElementById('march_scenario_creatures')?.checked || false;

            if (playerSopChecked) {
                scenariosList.push({ id: 'vs_sop', label: 'vs Player SOP' });
            } else if (keepChecked) {
                scenariosList.push({ id: 'vs_player', label: 'vs Player' });
            } else if (reinSopChecked) {
                scenariosList.push({ id: 'reinforce_sop', label: 'rein SOP' });
            } else if (creaturesChecked) {
                scenariosList.push({ id: 'general', label: 'Creatures' });
            } else {
                scenariosList.push({ id: 'vs_sop', label: 'vs Player SOP' });
            }

            let rowsHtml = '';
            let detailsHtml = '';

            scenariosList.forEach(sc => {
                const getGearMarchStats = (setData) => {
                    let flat = 0;
                    let percent = 0;
                    if (!setData) return { flat, percent };

                    const stacked = setData.stacked || {};
                    percent = setData.multipliers?.["March Size Percent"] || 0;

                    flat += (stacked["March Size"] || 0);

                    if (sc.id === 'vs_player') {
                        flat += (stacked["Single March Size vs Player"] || 0);
                        if (includeDragon) {
                            flat += (stacked["Dragon Max March Size"] || 0);
                            flat += (stacked["Dragon March Size vs Player"] || 0);
                        }
                    } else if (sc.id === 'vs_sop') {
                        flat += (stacked["Single March Size vs Player"] || 0);
                        flat += (stacked["March Size vs SOP"] || 0);
                        if (includeDragon) {
                            flat += (stacked["Dragon Max March Size"] || 0);
                            flat += (stacked["Dragon March Size vs Player"] || 0);
                            flat += (stacked["Dragon March Size vs SOP"] || 0);
                        }
                    } else if (sc.id === 'reinforce_sop') {
                        flat += (stacked["March Size Reinforcing SOP"] || 0);
                        if (includeDragon) {
                            flat += (stacked["Dragon Max March Size"] || 0);
                        }
                    } else if (sc.id === 'general') {
                        if (includeDragon) {
                            flat += (stacked["Dragon Max March Size"] || 0);
                        }
                    }

                    return { flat, percent };
                };

                const gear1 = getGearMarchStats(marchSizeData.set1);
                const gear2 = getGearMarchStats(marchSizeData.set2);

                const flat1 = unequippedFlat + gear1.flat;
                const pct1 = mpVal + gear1.percent;
                const total1 = flat1 * (1 + pct1 / 100);

                const flat2 = unequippedFlat + gear2.flat;
                const pct2 = mpVal + gear2.percent;
                const total2 = flat2 * (1 + pct2 / 100);

                const delta = Math.round(total2) - Math.round(total1);

                const deltaClass = delta > 0 ? 'positive' : (delta < 0 ? 'negative' : 'neutral');
                const deltaPrefix = delta > 0 ? '+' : '';
                const diffDisplay = delta !== 0 ? `${deltaPrefix}${fmt(delta)}` : '';

                rowsHtml += `
                    <tr>
                        <td>${sc.label}</td>
                        <td class="stat-val">${fmt(total1)}</td>
                        <td class="stat-val">${fmt(total2)}</td>
                        <td class="stat-diff ${deltaClass}">${diffDisplay}</td>
                    </tr>
                `;

                detailsHtml += `
                    <div class="march-formula-item" style="margin-top: 8px;">
                        * <strong>${sc.label}</strong>:
                        <div style="padding-left: 8px; margin-top: 2px;">
                            <strong>Baseline</strong>: (${fmt(unequippedFlat)} + ${fmt(gear1.flat)}) × (1 + (${fmtPct(mpVal)} + ${fmtPct(gear1.percent)})/100)
                        </div>
                        <div style="padding-left: 8px; margin-top: 2px;">
                            <strong>Target</strong>: (${fmt(unequippedFlat)} + ${fmt(gear2.flat)}) × (1 + (${fmtPct(mpVal)} + ${fmtPct(gear2.percent)})/100)
                        </div>
                    </div>
                `;
            });

            const cardHtml = `
                <div id="march_projection_card" class="march-projection-card">
                    <div class="march-projection-header">
                        <span>Projected March Size</span>
                    </div>
                    <div class="march-projection-scenarios">
                        <table class="comparison-table" aria-label="Projected March Size comparison">
                            <thead>
                                <tr>
                                    <th scope="col">Scenario</th>
                                    <th scope="col">Baseline</th>
                                    <th scope="col">Target</th>
                                    <th scope="col">Diff</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${rowsHtml}
                            </tbody>
                        </table>
                    </div>
                    <div class="march-projection-formula">
                        <button type="button" class="march-formula-toggle" id="march_formula_toggle_btn">
                            <span>Calculation Details</span>
                            <svg class="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                        <div class="march-formula-content collapsed" id="march_formula_content">
                            <div class="march-formula-item" style="font-weight: 600;">
                                Formula: (nogear_ms + gear_ms) × (1 + (bonus_pct + gear_pct) / 100)
                            </div>
                            <div class="march-formula-legend" style="opacity: 0.75; font-size: 0.78rem; margin: 4px 0 8px 0; line-height: 1.4;">
                                <div>Legend:</div>
                                <div style="padding-left: 8px;">• <strong>nogear_ms</strong>: March size (no gear)</div>
                                <div style="padding-left: 8px;">• <strong>gear_ms</strong>: Gear march size</div>
                                <div style="padding-left: 8px;">• <strong>bonus_pct</strong>: Percent bonuses</div>
                                <div style="padding-left: 8px;">• <strong>gear_pct</strong>: Gear percent</div>
                            </div>
                            <div class="march-formula-item" style="margin-top: 4px;">
                                * nogear_ms: ${fmt(unequippedFlat)} (derived from ${derivationLabel})
                            </div>
                            ${detailsHtml}
                        </div>
                    </div>
                </div>
            `;
            content.insertAdjacentHTML('beforeend', cardHtml);

            // Add toggle event listener for calculation details
            const toggleBtn = document.getElementById('march_formula_toggle_btn');
            const formulaContent = document.getElementById('march_formula_content');
            if (toggleBtn && formulaContent) {
                toggleBtn.addEventListener('click', () => {
                    const isCollapsed = formulaContent.classList.toggle('collapsed');
                    toggleBtn.classList.toggle('expanded', !isCollapsed);
                });
            }
        }
    }

    if (compactContainer) {
        if (isMarchCalculated) {
            compactContainer.classList.remove('hidden');
        } else {
            compactContainer.classList.add('hidden');
        }
    }

    // Render Totals section if 2 or more slots are being compared
    if (comparedSlotsCount >= 2) {
        const totalsHeader = document.createElement('h3');
        totalsHeader.className = 'slot-header totals-header';
        
        const titleSpan = document.createElement('span');
        titleSpan.textContent = 'All Equipment';
        totalsHeader.appendChild(titleSpan);
        
        totalsHeader.appendChild(createKebabDropdown('totals'));
        content.appendChild(totalsHeader);

        const totalsGrid = document.createElement('div');
        totalsGrid.className = 'comparison-grid totals-grid';

        if (totals.attack_sop && hasStats(totals.attack_sop)) totalsGrid.appendChild(createScenarioTable('Total: Attacking Player at SOP', totals.attack_sop, 'Baseline', 'Target'));
        if (totals.defense_sop && hasStats(totals.defense_sop)) totalsGrid.appendChild(createScenarioTable('Total: Defending Player at SOP', totals.defense_sop, 'Baseline', 'Target'));
        if (totals.balanced_sop && hasStats(totals.balanced_sop)) totalsGrid.appendChild(createScenarioTable('Total: Balanced', totals.balanced_sop, 'Baseline', 'Target'));
        if (totals.all_stats && hasStats(totals.all_stats)) totalsGrid.appendChild(createScenarioTable('Total: All Stats', totals.all_stats, 'Baseline', 'Target'));
        if (totals.dragon && hasStats(totals.dragon)) totalsGrid.appendChild(createScenarioTable('Total: Dragon', totals.dragon, 'Baseline', 'Target'));
        if (totals.march_size && hasStats(totals.march_size)) totalsGrid.appendChild(createScenarioTable('Total: March Size', totals.march_size, 'Baseline', 'Target'));
        if (totals.sop && hasStats(totals.sop)) totalsGrid.appendChild(createScenarioTable('Total: SOP', totals.sop, 'Baseline', 'Target'));
        if (totals.creatures && hasStats(totals.creatures)) totalsGrid.appendChild(createScenarioTable('Total: Creatures', totals.creatures, 'Baseline', 'Target'));

        content.appendChild(totalsGrid);

        const totalsDivider = document.createElement('div');
        totalsDivider.className = 'totals-divider';
        content.appendChild(totalsDivider);
    }

    // Iterate over slots (Individual Charts)
    slots.forEach(slot => {
        const slotData = data[slot];
        if (!slotData) return;

        const aSOP = slotData.attack_sop;
        const dSOP = slotData.defense_sop;

        if (!hasStats(aSOP) && !hasStats(dSOP) && !hasStats(slotData.dragon) && !hasStats(slotData.march_size) && !hasStats(slotData.sop) && !hasStats(slotData.creatures) && !hasStats(slotData.balanced_sop) && !hasStats(slotData.all_stats)) return;

        const abbr1 = getAbbreviation(set1Input?.[slot]);
        const abbr2 = getAbbreviation(set2Input?.[slot]);

        // Slot Header
        const slotHeader = document.createElement('h3');
        slotHeader.className = 'slot-header';
        
        const titleSpan = document.createElement('span');
        let formattedSlot = slot.replace('trinket', 'Trinket ');
        formattedSlot = formattedSlot.charAt(0).toUpperCase() + formattedSlot.slice(1);
        titleSpan.textContent = formattedSlot;
        slotHeader.appendChild(titleSpan);
        
        slotHeader.appendChild(createKebabDropdown(slot));
        content.appendChild(slotHeader);

        const grid = document.createElement('div');
        grid.className = 'comparison-grid';

        if (slotData.attack_sop && hasStats(slotData.attack_sop)) {
            grid.appendChild(createScenarioTable('Attacking Player at SOP', slotData.attack_sop, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.defense_sop && hasStats(slotData.defense_sop)) {
            grid.appendChild(createScenarioTable('Defending Player at SOP', slotData.defense_sop, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.balanced_sop && hasStats(slotData.balanced_sop)) {
            grid.appendChild(createScenarioTable('Balanced', slotData.balanced_sop, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.all_stats && hasStats(slotData.all_stats)) {
            grid.appendChild(createScenarioTable('All Stats', slotData.all_stats, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.dragon && hasStats(slotData.dragon)) {
            grid.appendChild(createScenarioTable('Dragon', slotData.dragon, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.march_size && hasStats(slotData.march_size)) {
            grid.appendChild(createScenarioTable('March Size', slotData.march_size, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.sop && hasStats(slotData.sop)) {
            grid.appendChild(createScenarioTable('SOP', slotData.sop, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        if (slotData.creatures && hasStats(slotData.creatures)) {
            grid.appendChild(createScenarioTable('Creatures', slotData.creatures, abbr1, abbr2, slotData.class1, slotData.class2));
        }

        content.appendChild(grid);
    });

    // Scroll to results
    container.scrollIntoView({ behavior: 'smooth' });
}

function createScenarioTable(title, scenarioData, abbr1, abbr2, class1, class2) {
    const diffs = scenarioData.diffs;
    const s1 = scenarioData.set1;
    const s2 = scenarioData.set2;

    const wrapper = document.createElement('div');
    wrapper.className = 'scenario-container';

    // Title
    const titleEl = document.createElement('h4');
    titleEl.className = 'scenario-title';
    titleEl.textContent = title;
    wrapper.appendChild(titleEl);

    // SOP Logic Setup
    const isSOPKey = (key) => key && (key.toLowerCase().includes('sop') || key.toLowerCase().includes('reinforce'));
    let includeSOP = true;
    let hasSOPStats = false;

    const checkSOP = (s) => {
        if (!s) return false;
        if (s.stacked && Object.keys(s.stacked).some(isSOPKey)) return true;
        if (s.health && Object.keys(s.health).some(isSOPKey)) return true;
        if (s.meta && Object.keys(s.meta).some(isSOPKey)) return true;
        return false;
    };

    const isMarcherKey = (key) => key && (key.toLowerCase().includes('marching') || key.toLowerCase().includes('marcher'));
    let isDefense = false; // Default state for Balanced

    if (title === 'Attacking Player at SOP' || title === 'Defending Player at SOP' || title === 'Balanced' || title === 'March Size' || title === 'Total: Attacking Player at SOP' || title === 'Total: Defending Player at SOP' || title === 'Total: Balanced' || title === 'Total: March Size' || title === 'All Stats' || title === 'Total: All Stats') {
        hasSOPStats = checkSOP(s1) || checkSOP(s2);
    }

    // Arena Logic Setup
    const isArenaKey = (key) => {
        if (!key) return false;
        const baseKey = typeof key === 'string' && key.includes('|') ? key.split('|')[0] : key;
        return baseKey.toLowerCase().includes('arena');
    };
    let includeArena = true;
    let hasArenaStats = false;

    const checkArena = (s) => {
        if (!s) return false;
        if (s.stacked && Object.keys(s.stacked).some(isArenaKey)) return true;
        if (s.meta && s.meta.has_arena_stats) return true;
        return false;
    };

    if (title === 'Creatures' || title === 'Total: Creatures' || title === 'All Stats' || title === 'Total: All Stats') {
        hasArenaStats = checkArena(s1) || checkArena(s2);
    }

    const getDynamicTitle = () => {
        let currentTitle = title;
        
        if (currentTitle.includes('Balanced')) {
            currentTitle = currentTitle.replace('Balanced', isDefense ? 'Balanced Defender' : 'Balanced Marcher');
        }

        if (includeSOP) return currentTitle;

        if (currentTitle.includes('Attacking Player at SOP')) {
            return currentTitle.replace('Attacking Player at SOP', 'Attack vs Player');
        }
        if (currentTitle.includes('Defending Player at SOP')) {
            return currentTitle.replace('Defending Player at SOP', 'Defense vs Player');
        }
        
        return currentTitle;
    };

    titleEl.textContent = getDynamicTitle();

    // Main Stats Table
    const table = document.createElement('table');
    table.className = 'comparison-table';
    table.setAttribute('aria-label', `${title} comparison`);

    // Visually hidden caption for screen readers
    const caption = document.createElement('caption');
    caption.className = 'sr-only';
    caption.textContent = `${title}: Baseline (${abbr1}) vs Target (${abbr2})`;
    table.appendChild(caption);

    // Header
    const thead = document.createElement('thead');
    thead.innerHTML = `
        <tr>
            <th scope="col">Stat</th>
            <th scope="col">${abbr1}</th>
            <th scope="col">${abbr2}</th>
            <th scope="col">Diff</th>
        </tr>
    `;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    table.appendChild(tbody);
    wrapper.appendChild(table);

    const classesPresent = new Set();

    const extractClassesFromKeys = (s) => {
        if (!s) return;
        Object.keys(s).forEach(k => {
            if (k && k.includes('|')) {
                const parts = k.split('|');
                const resolved = getStatTargetClass(parts[0], parts[1]);
                if (resolved) classesPresent.add(resolved);
            }
        });
    };

    extractClassesFromKeys(s1.stacked);
    extractClassesFromKeys(s2.stacked);
    extractClassesFromKeys(s1.health);
    extractClassesFromKeys(s2.health);
    extractClassesFromKeys(s1.multipliers);
    extractClassesFromKeys(s2.multipliers);

    const collectSlotClasses = (s, cls) => {
        if (!s) return;
        for (const key of classSpecificStats) {
            const hasInStacked = s.stacked && s.stacked[key] !== undefined && s.stacked[key] !== 0;
            const hasInHealth = s.health && s.health[key] !== undefined && s.health[key] !== 0;
            if (hasInStacked || hasInHealth) {
                const resolved = getStatTargetClass(key, cls);
                if (resolved) classesPresent.add(resolved);
            }
        }
    };

    collectSlotClasses(s1, class1);
    collectSlotClasses(s2, class2);

    let activeClasses = new Set(classesPresent);



    const renderTable = () => {
        tbody.innerHTML = '';

        const getPrecisionFlags = (k) => {
            const keyLower = k ? k.toLowerCase() : '';
            const isPercent = k && (keyLower.includes('percent') || keyLower.includes('speed'));
            const isMarch = k && keyLower.includes('march') && !keyLower.includes('speed') && !isPercent;
            const isCapacity = keyLower.includes('capacity') || keyLower.includes('troops');
            const isWhole = !isPercent && (title.includes('March Size') || title.includes('SOP') || title.includes('Balanced') || isMarch || isCapacity || keyLower.includes('rallied') || keyLower.includes('reinforcement') || keyLower.includes('total_'));
            return { isPercent, isWhole };
        };

        const roundToPrecision = (val, isPercent, isWhole) => {
            const digits = isPercent ? 2 : (isWhole ? 0 : 1);
            const factor = Math.pow(10, digits);
            return Math.round(val * factor) / factor;
        };

        const fmt = (v, k) => {
            if (v === 0) return '';
            const { isPercent, isWhole } = getPrecisionFlags(k);
            return formatNumberLocally(v, isPercent, isWhole);
        };

        let s1TotalBase = 0, s2TotalBase = 0;
        let s1TotalDef = 0, s2TotalDef = 0;
        let s1TotalHealth = 0, s2TotalHealth = 0;
        let s1TotalDH = 0, s2TotalDH = 0;

        const getEffectiveVal = (val, rawKey, cls) => {
            if (val === undefined) return 0;
            let baseKey = rawKey;
            let targetCls = cls;
            if (rawKey && typeof rawKey === 'string' && rawKey.includes('|')) {
                const parts = rawKey.split('|');
                baseKey = parts[0];
                targetCls = parts[1] === 'None' ? null : parts[1];
            }
            targetCls = getStatTargetClass(baseKey, targetCls);

            if (!includeSOP && isSOPKey(baseKey)) return 0;
            if (!includeArena && isArenaKey(baseKey)) return 0;
            if (isDefense && isMarcherKey(baseKey)) return 0; // Filter marcher stats out completely
            if (targetCls && targetCls.toLowerCase() === 'dragon') return val;
            if (classSpecificStats.includes(baseKey) && targetCls && !activeClasses.has(targetCls)) return 0;
            return val;
        };

        if (s1.meta.total_base !== undefined || (s2 && s2.meta.total_base !== undefined)) {
            Object.keys(s1.stacked || {}).forEach(k => { s1TotalBase += getEffectiveVal(s1.stacked[k], k, class1); });
            Object.keys(s2.stacked || {}).forEach(k => { s2TotalBase += getEffectiveVal(s2.stacked[k], k, class2); });
        }
        if (s1.meta.total_defense !== undefined || (s2 && s2.meta.total_defense !== undefined)) {
            Object.keys(s1.stacked || {}).forEach(k => { s1TotalDef += getEffectiveVal(s1.stacked[k], k, class1); });
            Object.keys(s2.stacked || {}).forEach(k => { s2TotalDef += getEffectiveVal(s2.stacked[k], k, class2); });
        }
        if (s1.meta.total_health !== undefined || (s2 && s2.meta.total_health !== undefined)) {
            Object.keys(s1.health || {}).forEach(k => { s1TotalHealth += getEffectiveVal(s1.health[k], k, class1); });
            Object.keys(s2.health || {}).forEach(k => { s2TotalHealth += getEffectiveVal(s2.health[k], k, class2); });
        }
        if (s1.meta.total_dh !== undefined || (s2 && s2.meta.total_dh !== undefined)) {
            ['Dragon Defense vs Dragons', 'Dragon Health vs Dragons'].forEach(k => {
                s1TotalDH += getEffectiveVal(s1.stacked[k], k, class1);
                s2TotalDH += getEffectiveVal(s2.stacked[k], k, class2);
            });
        }

        const createRow = (label, rawKey, type = 'stacked', customV1, customV2) => {
            let rowCls1 = class1, rowCls2 = class2;
            if (rawKey && typeof rawKey === 'string' && rawKey.includes('|')) {
                const parts = rawKey.split('|');
                if (!type.startsWith('meta')) label = parts[0];
                rowCls1 = parts[1] === 'None' ? null : parts[1];
                rowCls2 = rowCls1;
            }
            const key = rawKey && typeof rawKey === 'string' && rawKey.includes('|') ? rawKey.split('|')[0] : rawKey;
            if (type !== 'meta' && type !== 'meta_with_class') {
                rowCls1 = getStatTargetClass(key, rowCls1);
                rowCls2 = getStatTargetClass(key, rowCls2);
            }

            const row = document.createElement('tr');
            row.dataset.statType = type;
            if (type === 'base' || type.startsWith('meta')) row.className = 'base-row';

            if (label === 'Dragon Defense vs Dragons') label = 'Dragon Defense';
            if (label === 'Dragon Health vs Dragons') label = 'Dragon Health';
            if (label === 'Dragon Attack Base') label = 'Dragon Attack';
            if (label === 'Troop Attack in the Arena') label = 'Troop Attack Arena';
            if (label === 'Cavalry Attack in the Arena') label = 'Cav Attack Arena';
            if (label === 'Ranged Attack in the Arena') label = 'Rng Attack Arena';
            if (label === 'Infantry Attack in the Arena') label = 'Inf Attack Arena';

            const preserveDragonBaseKeys = [
                'Dragon Defense vs Dragons', 'Dragon Health vs Dragons', 'Dragon Attack vs Dragons', 
                'Dragon Attack Base', 'Dragon Attack vs Infantry', 
                'Dragon Attack vs Cavalry', 'Dragon Attack vs Ranged'
            ];

            if (title !== 'Dragon' && label.startsWith('Dragon ') && !preserveDragonBaseKeys.includes(key)) {
                label = label.replace(/^Dragon /, 'Dragon: ');
            }

            if (label === 'Rally Troop Capacity') {
                label = 'Rally Cap';
            }

            if (window.innerWidth <= 768) {
                if (label === 'Dragon Attack vs Dragons') {
                    label = 'DvD';
                }

                label = label.replace(/Dragon/g, 'Drag');
                label = label.replace(/While Marching vs Player/g, 'Marching');
                label = label.replace(/Marcher (.*?) vs Player/g, '$1 Marching');
                label = label.replace(/vs Player at SOP/g, 'vs PL SOP');
                label = label.replace(/vs Player/g, 'vs PL');
                label = label.replace(/Reinforcement/g, 'Rein');
                label = label.replace(/Capacity/g, 'Cap');
                label = label.replace(/Percent/g, '%');
            }

            let v1 = 0, v2 = 0;

            if (type === 'meta' || type === 'meta_with_class') {
                v1 = customV1 !== undefined ? customV1 : (s1.meta[key] || 0);
                v2 = customV2 !== undefined ? customV2 : (s2.meta[key] || 0);
            } else if (type === 'multipliers') {
                v1 = getEffectiveVal(s1.multipliers[rawKey] || 0, rawKey, rowCls1);
                v2 = getEffectiveVal(s2.multipliers[rawKey] || 0, rawKey, rowCls2);
            } else {
                let rawV1 = s1.stacked[rawKey];
                if (rawV1 === undefined && s1.health) rawV1 = s1.health[rawKey];
                v1 = getEffectiveVal(rawV1, rawKey, rowCls1);

                let rawV2 = s2.stacked[rawKey];
                if (rawV2 === undefined && s2.health) rawV2 = s2.health[rawKey];
                v2 = getEffectiveVal(rawV2, rawKey, rowCls2);
            }

            const { isPercent: isPercentKey, isWhole } = getPrecisionFlags(key);
            const roundedV1 = roundToPrecision(v1, isPercentKey, isWhole);
            const roundedV2 = roundToPrecision(v2, isPercentKey, isWhole);
            const effectiveDiff = roundToPrecision(roundedV2 - roundedV1, isPercentKey, isWhole);

            const diffClass = effectiveDiff > 0 ? 'positive' : (effectiveDiff < 0 ? 'negative' : 'neutral');
            const sign = effectiveDiff > 0 ? '+' : '';
            let diffDisplay = '';
            if (effectiveDiff !== 0) {
                // Format the number and prepend '+' if positive
                const formatted = formatNumberLocally(effectiveDiff, isPercentKey, isWhole);
                diffDisplay = effectiveDiff > 0 ? `+${formatted}` : formatted;
            }

            let v1Html = fmt(v1, key);
            let v2Html = fmt(v2, key);

            let showBadge = type !== 'meta' && classSpecificStats.includes(key);
            if (type === 'meta_with_class' && rowCls1) showBadge = true;

            if (showBadge && classesPresent.size > 1) {
                if (v1 !== 0 && rowCls1) {
                    v1Html = `<div class="stat-value-container"><span>${v1Html}</span>${getIndicatorHtml(rowCls1)}</div>`;
                }
                if (v2 !== 0 && rowCls2) {
                    v2Html = `<div class="stat-value-container"><span>${v2Html}</span>${getIndicatorHtml(rowCls2)}</div>`;
                }
            }

            row.innerHTML = `
                <td>${label}</td>
                <td class="stat-val">${v1Html}</td>
                <td class="stat-val">${v2Html}</td>
                <td class="stat-diff ${diffClass}">${diffDisplay}</td>
            `;
            return row;
        };

        let stackedKeys = new Set([...Object.keys(s1.stacked), ...Object.keys(s2.stacked)]);
        if (s1.health) {
            const hKeys = [...Object.keys(s1.health), ...Object.keys(s2.health || {})];
            hKeys.forEach(k => stackedKeys.add(k));
        }

        let stackedRows = [];
        
        const getStatDetails = (k) => {
            let key = k.toLowerCase().trim();
            
            // 1. Rally & Reinforcement Group (group 70)
            if (key.includes('rally') || key.includes('rallied') || key.includes('reinforce') || key.includes('reinforcement')) {
                let sub = 1;
                if (key.includes('rallied')) sub = 2;
                else if (key.includes('reinforce') || key.includes('reinforcement')) sub = 3;
                return { group: 70, type: 4, sub: sub };
            }
            
            // 2. March Speed Group (group 60)
            if (key.includes('speed')) {
                let sub = 1;
                if (key.includes('dragon')) sub = 2;
                return { group: 60, type: 4, sub: sub };
            }
            
            // 3. Dragon March Size Group (group 50)
            if (key.includes('dragon') && key.includes('march size')) {
                let sub = 1;
                if (key.includes('player')) sub = 2;
                else if (key.includes('sop')) sub = 3;
                return { group: 50, type: 4, sub: sub };
            }
            
            // 4. Troop March Size Group (group 40)
            if (key.includes('march size')) {
                let sub = 1;
                if (key.includes('player')) sub = 2;
                else if (key.includes('sop')) sub = 3;
                return { group: 40, type: 4, sub: sub };
            }

            // 5. Core Combat Groups (10: General, 20: Troop, 30: Dragon)
            let group = 10;
            if (key.includes('dragon')) {
                group = 30;
            } else if (key.includes('troop')) {
                group = 20;
            }

            // Determine type: Attack (1), Defense (2), Health (3), Other (4)
            let type = 4;
            if (key.includes('attack')) type = 1;
            else if (key.includes('defense')) type = 2;
            else if (key.includes('health')) type = 3;

            // Determine sub-group order: Base (1) -> vs Player (2) -> SOP (3) -> Marching/Defending (4) -> Specialized/Other (5)
            let sub = 5;
            if (key === 'attack' || key === 'defense' || key === 'health' ||
                key === 'troop attack' || key === 'troop defense' || key === 'troop health' ||
                key === 'dragon attack' || key === 'dragon defense' || key === 'dragon health' ||
                key === 'dragon attack base') {
                sub = 1;
            } else if (key.includes('marching') || key.includes('marcher') || key.includes('defending')) {
                sub = 4;
            } else if (key.includes('sop') || key.includes('at sop')) {
                sub = 3;
            } else if (key.includes('vs player')) {
                sub = 2;
            }

            return { group, type, sub };
        };

        const customSort = (rawA, rawB) => {
            const a = typeof rawA === 'string' && rawA.includes('|') ? rawA.split('|')[0] : rawA;
            const b = typeof rawB === 'string' && rawB.includes('|') ? rawB.split('|')[0] : rawB;

            const detailsA = getStatDetails(a);
            const detailsB = getStatDetails(b);

            if (detailsA.group !== detailsB.group) {
                return detailsA.group - detailsB.group;
            }
            if (detailsA.type !== detailsB.type) {
                return detailsA.type - detailsB.type;
            }
            if (detailsA.sub !== detailsB.sub) {
                return detailsA.sub - detailsB.sub;
            }
            return rawA.localeCompare(rawB);
        };

        Array.from(stackedKeys).sort(customSort).forEach(key => {
            if (!(!includeSOP && isSOPKey(key)) && !(!includeArena && isArenaKey(key))) {
                let v1 = 0, v2 = 0;
                let rawV1 = s1.stacked[key];
                if (rawV1 === undefined && s1.health) rawV1 = s1.health[key];
                v1 = getEffectiveVal(rawV1, key, class1);

                let rawV2 = s2.stacked[key];
                if (rawV2 === undefined && s2.health) rawV2 = s2.health[key];
                v2 = getEffectiveVal(rawV2, key, class2);

                if (v1 !== 0 || v2 !== 0) {
                    const r = createRow(key, key);
                    tbody.appendChild(r);
                    stackedRows.push(r);
                }
            }
        });

        // Apply dynamic zebra striping only to tables with >=4 stats
        if (stackedRows.length >= 4) {
            stackedRows.forEach((row, idx) => {
                // visually alternate: index 1 is visual row 2 (even)
                if (idx % 2 === 1) {
                    row.classList.add('zebra');
                }
            });
        }

        if (s1TotalBase !== 0 || s2TotalBase !== 0) {
            tbody.appendChild(createRow('Base', 'total_base', 'meta', s1TotalBase, s2TotalBase));
        }
        if (s1TotalDef !== 0 || s2TotalDef !== 0) {
            tbody.appendChild(createRow('Total Defense', 'total_defense', 'meta', s1TotalDef, s2TotalDef));
        }
        if (s1TotalHealth !== 0 || s2TotalHealth !== 0) {
            tbody.appendChild(createRow('Total Health', 'total_health', 'meta', s1TotalHealth, s2TotalHealth));
        }
        if (s1TotalDH !== 0 || s2TotalDH !== 0) {
            tbody.appendChild(createRow('Base D/H', 'total_dh', 'meta', s1TotalDH, s2TotalDH));
        }

        // March Size meta fields
        const marchMetaKeys = [
            { key: 'march_vs_keep', label: 'vs Player' },
            { key: 'march_vs_sop', label: 'vs Player SOP' },
            { key: 'march_reinforce_sop', label: 'rein SOP' },
            { key: 'drag_vs_keep', label: 'Drag vs Player' },
            { key: 'drag_vs_sop', label: 'Drag vs Player SOP' },
            { key: 'drag_reinforce_sop', label: 'Drag rein SOP' }
        ];

        const sopMetaKeys = [
            { key: 'rally_cap_vs_sop', label: 'Rally Cap vs SOP' }
        ];

        const creaturesMetaKeys = [
            { key: 'attack_vs_creature', label: 'Attack vs Creature' },
            { key: 'dh_vs_creature', label: 'D/H vs Creature' },
            { key: 'march_vs_creature', label: 'March vs Creature' },
            { key: 'drag_attack_vs_creature', label: 'Drag Attack vs Creature' },
            { key: 'drag_dh_vs_creature', label: 'Drag D/H vs Creature' },
            { key: 'drag_march_vs_creature', label: 'Drag March vs Creature' },
            { key: 'attack_vs_dead', label: 'Attack vs Dead' },
            { key: 'dh_vs_dead', label: 'D/H vs Dead' },
            { key: 'drag_attack_vs_dead', label: 'Drag Attack vs Dead' },
            { key: 'drag_dh_vs_dead', label: 'Drag D/H vs Dead' },
            { key: 'attack_in_arena', label: 'Attack Arena' },
            { key: 'drag_attack_in_arena', label: 'Drag Attack Arena' }
        ];

        const isCreatureScenario = (title === 'Creatures' || title === 'Total: Creatures');
        const computeCreatureMeta = (s, defaultCls) => {
            if (!s || !s.stacked) return {};
            const sumKeys = (targetBaseKeys) => {
                let total = 0;
                for (const [rawK, val] of Object.entries(s.stacked)) {
                    const baseK = rawK.includes('|') ? rawK.split('|')[0] : rawK;
                    if (targetBaseKeys.includes(baseK)) {
                        total += getEffectiveVal(val, rawK, defaultCls);
                    }
                }
                return total;
            };

            const baseAtk = sumKeys(["Attack", "Troop Attack"]);
            const baseDef = sumKeys(["Defense", "Troop Defense"]);
            const baseHlth = sumKeys(["Health", "Troop Health"]);
            const march = sumKeys(["March Size"]);

            const cAtk = sumKeys(["Attack vs Creature"]);
            const cDef = sumKeys(["Defense vs Creature"]);
            const cHlth = sumKeys(["Health vs Creature"]);
            const cMarch = sumKeys(["March Size vs Creatures"]);

            const cAtkTotal = baseAtk + cAtk;
            const cDhTotal = baseDef + baseHlth + cDef + cHlth;
            const cMarchTotal = march + cMarch;

            const dAtk = sumKeys(["Attack vs Dead", "Troop Attack vs Dead"]);
            const dDef = sumKeys(["Defense vs Dead", "Troop Defense vs Dead"]);
            const dHlth = sumKeys(["Health vs Dead", "Troop Health vs Dead"]);

            const deadAtkTotal = baseAtk + dAtk;
            const deadDhTotal = baseDef + baseHlth + dDef + dHlth;

            const arenaAtk = sumKeys([
                "Troop Attack in the Arena",
                "Cavalry Attack in the Arena",
                "Ranged Attack in the Arena",
                "Infantry Attack in the Arena"
            ]);
            const arenaAtkTotal = cAtkTotal + arenaAtk;

            const baseDragAtk = sumKeys(["Dragon Attack", "Dragon Troop Attack"]);
            const baseDragDh = sumKeys(["Dragon Defense", "Dragon Health", "Dragon Troop Defense", "Dragon Troop Health"]);
            const dMarch = sumKeys(["Dragon Max March Size"]);

            const dragCAtk = sumKeys(["Dragon Attack vs Creature"]);
            const dragCDh = sumKeys(["Dragon Defense vs Creature", "Dragon Health vs Creature"]);

            const dCAtkTotal = cAtkTotal + baseDragAtk + dragCAtk;
            const dCDhTotal = cDhTotal + baseDragDh + dragCDh;
            const dCMarchTotal = cMarchTotal + dMarch;

            const dragDAtk = sumKeys(["Dragon Attack vs Dead"]);
            const dragDDh = sumKeys(["Dragon Defense vs Dead", "Dragon Health vs Dead"]);

            const deadDragAtkTotal = deadAtkTotal + baseDragAtk + dragDAtk;
            const deadDragDhTotal = deadDhTotal + baseDragDh + dragDDh;

            const arenaDragAtkTotal = dCAtkTotal + arenaAtk;

            const meta = {};
            if (cAtkTotal > 0) meta.attack_vs_creature = cAtkTotal;
            if (cDhTotal > 0) meta.dh_vs_creature = cDhTotal;
            if (cMarchTotal > 0) meta.march_vs_creature = cMarchTotal;

            if (dCAtkTotal > cAtkTotal || dCDhTotal > cDhTotal || dCMarchTotal > cMarchTotal) {
                if (dCAtkTotal > 0) meta.drag_attack_vs_creature = dCAtkTotal;
                if (dCDhTotal > 0) meta.drag_dh_vs_creature = dCDhTotal;
                if (dCMarchTotal > 0) meta.drag_march_vs_creature = dCMarchTotal;
            }

            if (deadAtkTotal > 0) meta.attack_vs_dead = deadAtkTotal;
            if (deadDhTotal > 0) meta.dh_vs_dead = deadDhTotal;
            if (deadDragAtkTotal > deadAtkTotal || deadDragDhTotal > deadDhTotal) {
                if (deadDragAtkTotal > 0) meta.drag_attack_vs_dead = deadDragAtkTotal;
                if (deadDragDhTotal > 0) meta.drag_dh_vs_dead = deadDragDhTotal;
            }

            if (arenaAtkTotal > 0) meta.attack_in_arena = arenaAtkTotal;
            if (arenaDragAtkTotal > arenaAtkTotal && arenaDragAtkTotal > 0) {
                meta.drag_attack_in_arena = arenaDragAtkTotal;
            }

            return meta;
        };

        const dynCreatureMeta1 = isCreatureScenario ? computeCreatureMeta(s1, class1) : null;
        const dynCreatureMeta2 = isCreatureScenario ? computeCreatureMeta(s2, class2) : null;

        [...marchMetaKeys, ...sopMetaKeys, ...creaturesMetaKeys].forEach(({ key, label }) => {
            if (!includeSOP && isSOPKey(key)) return;
            if (!includeArena && isArenaKey(key)) return;

            if (key.includes('dead')) {
                const hasDead = (s1.meta && s1.meta.has_dead_stats) || (s2 && s2.meta && s2.meta.has_dead_stats);
                if (!hasDead) return;
            }

            if (key.includes('arena')) {
                const hasArena = (s1.meta && s1.meta.has_arena_stats) || (s2 && s2.meta && s2.meta.has_arena_stats);
                if (!hasArena) return;
            }

            const meta1 = (isCreatureScenario && dynCreatureMeta1) ? dynCreatureMeta1 : (s1.meta || {});
            const meta2 = (isCreatureScenario && dynCreatureMeta2) ? dynCreatureMeta2 : (s2.meta || {});

            if (meta1[key] !== undefined || meta2[key] !== undefined) {
                let customV1 = meta1[key];
                let customV2 = meta2[key];

                if (key.startsWith('drag_')) {
                    const baseKey = key.replace('drag_', 'march_');
                    if (customV1 === undefined) customV1 = meta1[baseKey] || 0;
                    if (customV2 === undefined) customV2 = meta2[baseKey] || 0;
                }

                if ((customV1 !== 0 && customV1 !== undefined) || (customV2 !== 0 && customV2 !== undefined)) {
                    tbody.appendChild(createRow(label, key, 'meta', customV1, customV2));
                }
            }
        });


        // Custom Balanced Table Evaluator
        if (title.includes('Balanced')) {
            const isAttackKey = (k) => k && (k.toLowerCase().includes('attack'));
            const isDefenseKey = (k) => k && (k.toLowerCase().includes('defense') && !k.toLowerCase().includes('dragon defense vs dragons'));
            const isHealthKey = (k) => k && (k.toLowerCase().includes('health') && !k.toLowerCase().includes('dragon health vs dragons'));

            let balSums1 = { Attack: {}, Defense: {}, Health: {} };
            let balSums2 = { Attack: {}, Defense: {}, Health: {} };

            const extractClass = (rawKey, defaultCls) => {
                if (typeof rawKey === 'string' && rawKey.includes('|')) {
                    const cls = rawKey.split('|')[1];
                    if (cls !== 'None') return cls;
                }
                let baseK = rawKey && typeof rawKey === 'string' && rawKey.includes('|') ? rawKey.split('|')[0] : rawKey;
                if (classSpecificStats.includes(baseK)) return defaultCls;
                return 'Generic';
            };

            const accumulate = (s, defaultCls, sumsObj, isHealthObj = false) => {
                Object.keys(s || {}).forEach(k => {
                    const val = getEffectiveVal(s[k], k, defaultCls);
                    if (val !== 0) {
                        const baseKey = k && typeof k === 'string' && k.includes('|') ? k.split('|')[0] : k;
                        const cat = extractClass(k, defaultCls) || 'Generic';
                        const finalCat = cat.toLowerCase() === 'dragon' ? 'Generic' : cat;

                        if (isAttackKey(baseKey)) sumsObj.Attack[finalCat] = (sumsObj.Attack[finalCat] || 0) + val;
                        if (!isHealthObj && isDefenseKey(baseKey)) sumsObj.Defense[finalCat] = (sumsObj.Defense[finalCat] || 0) + val;
                        if (isHealthObj && isHealthKey(baseKey)) sumsObj.Health[finalCat] = (sumsObj.Health[finalCat] || 0) + val;
                    }
                });
            };

            accumulate(s1.stacked, class1, balSums1);
            accumulate(s2.stacked, class2, balSums2);
            accumulate(s1.health, class1, balSums1, true);
            accumulate(s2.health, class2, balSums2, true);

            ['Attack', 'Defense', 'Health'].forEach(statType => {
                const cats = new Set([...Object.keys(balSums1[statType]), ...Object.keys(balSums2[statType])]);
                // Sort generic first
                Array.from(cats).sort((a, b) => a === 'Generic' ? -1 : b === 'Generic' ? 1 : a.localeCompare(b)).forEach(cat => {
                    const v1 = balSums1[statType][cat] || 0;
                    const v2 = balSums2[statType][cat] || 0;
                    if (v1 > 0 || v2 > 0) {
                        const rowKey = cat === 'Generic' ? statType : `${statType}|${cat}`;
                        tbody.appendChild(createRow(`Total ${statType}`, rowKey, 'meta_with_class', v1, v2));
                    }
                });
            });
        }

        const multKeys = new Set([...Object.keys(s1.multipliers), ...Object.keys(s2.multipliers)]);
        if (multKeys.size > 0) {
            const multSort = (rawA, rawB) => {
                const a = typeof rawA === 'string' && rawA.includes('|') ? rawA.split('|')[0] : rawA;
                const b = typeof rawB === 'string' && rawB.includes('|') ? rawB.split('|')[0] : rawB;

                const clsA = typeof rawA === 'string' && rawA.includes('|') ? rawA.split('|')[1] : '';
                const clsB = typeof rawB === 'string' && rawB.includes('|') ? rawB.split('|')[1] : '';

                const gearClassScore = (cls) => {
                    if (!cls || cls === 'None') return 0;
                    const c = cls.toLowerCase();
                    if (c.includes('infantry')) return 1;
                    if (c.includes('cavalry')) return 2;
                    if (c.includes('ranged')) return 3;
                    return 4;
                };

                const typeScore = (key) => {
                    const k = key.toLowerCase();
                    if (k.includes('march size %')) return 0; // Highest priority
                    if (k.includes('attack')) return 1;
                    if (k.includes('defense')) return 2;
                    if (k.includes('health')) return 3;
                    return 4;
                };

                const targetTypeScore = (key) => {
                    const k = key.toLowerCase();
                    if (k.includes('infantry')) return 1;
                    if (k.includes('cavalry')) return 2;
                    if (k.includes('ranged')) return 3;
                    return 4;
                };

                const gcA = gearClassScore(clsA);
                const gcB = gearClassScore(clsB);
                if (gcA !== gcB) return gcA - gcB;

                const stA = typeScore(a);
                const stB = typeScore(b);
                if (stA !== stB) return stA - stB;

                const ttA = targetTypeScore(a);
                const ttB = targetTypeScore(b);
                if (ttA !== ttB) return ttA - ttB;

                return rawA.localeCompare(rawB);
            };

            const multRows = [];
            Array.from(multKeys).sort(multSort).forEach(key => {
                const v1 = getEffectiveVal(s1.multipliers[key] || 0, key, class1);
                const v2 = getEffectiveVal(s2.multipliers[key] || 0, key, class2);
                if (v1 !== 0 || v2 !== 0) {
                    multRows.push(createRow(key, key, 'multipliers'));
                }
            });

            if (multRows.length > 0) {
                const hRow = document.createElement('tr');
                hRow.innerHTML = `<th colspan="4" class="multipliers-header-cell">Multipliers</th>`;
                tbody.appendChild(hRow);
                
                multRows.forEach((row, idx) => {
                    // Apply zebra striping based on index (odd indices get zebra background)
                    if (idx % 2 === 1) {
                        row.classList.add('zebra');
                    }
                    tbody.appendChild(row);
                });
            }
        }
    };

    renderTable();

    let globalStackedKeys = new Set([...Object.keys(s1.stacked || {}), ...Object.keys(s2.stacked || {})]);
    if (s1.health) [...Object.keys(s1.health)].forEach(k => globalStackedKeys.add(k));
    if (s2.health) [...Object.keys(s2.health)].forEach(k => globalStackedKeys.add(k));
    
    let hasGenericStats = false;
    for (let key of globalStackedKeys) {
        let baseKey = key && typeof key === 'string' && key.includes('|') ? key.split('|')[0] : key;
        if (!classSpecificStats.includes(baseKey) && baseKey !== 'Dragon Attack vs Dragons') {
            let v1 = (s1.stacked && s1.stacked[key] !== undefined) ? s1.stacked[key] : (s1.health && s1.health[key]);
            let v2 = (s2.stacked && s2.stacked[key] !== undefined) ? s2.stacked[key] : (s2.health && s2.health[key]);
            if (v1 || v2) {
                hasGenericStats = true;
                break;
            }
        }
    }

    const showTroop = hasGenericStats;

    const hasNonArenaClassStat = (cls) => {
        const checkDict = (dict, defaultCls) => {
            if (!dict) return false;
            for (const [rawKey, val] of Object.entries(dict)) {
                if (!val) continue;
                const baseKey = rawKey.includes('|') ? rawKey.split('|')[0] : rawKey;
                const explicitCls = rawKey.includes('|') ? rawKey.split('|')[1] : null;
                if (!isArenaKey(baseKey) && classSpecificStats.includes(baseKey)) {
                    const resolved = getStatTargetClass(baseKey, explicitCls || defaultCls);
                    if (resolved === cls) return true;
                }
            }
            return false;
        };
        return checkDict(s1.stacked, class1) || checkDict(s2.stacked, class2) ||
               checkDict(s1.health, class1) || checkDict(s2.health, class2);
    };

    if (showTroop || hasSOPStats || hasArenaStats || title.includes('Balanced')) {
        const toggleWrapper = document.createElement('div');
        toggleWrapper.className = 'scenario-all-toggles';

        const togglesList = document.createElement('div');
        togglesList.className = 'scenario-toggles-list';
        toggleWrapper.appendChild(togglesList);

        const arenaOnlyTroopLabels = [];

        if (title.includes('Balanced')) {
            const defLabel = document.createElement('label');
            defLabel.className = 'toggle-label';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.checked = isDefense;

            checkbox.addEventListener('change', (e) => {
                isDefense = e.target.checked;
                titleEl.textContent = getDynamicTitle();
                renderTable();
            });

            const span = document.createElement('span');
            span.textContent = 'Defense';

            defLabel.appendChild(checkbox);
            defLabel.appendChild(span);
            togglesList.appendChild(defLabel);
        }

        if (hasSOPStats) {
            const sopLabel = document.createElement('label');
            sopLabel.className = 'toggle-label';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.checked = true;

            checkbox.addEventListener('change', (e) => {
                includeSOP = e.target.checked;
                titleEl.textContent = getDynamicTitle();
                renderTable();
            });

            const span = document.createElement('span');
            span.textContent = 'SOP Stats';

            sopLabel.appendChild(checkbox);
            sopLabel.appendChild(span);
            togglesList.appendChild(sopLabel);
        }

        if (hasArenaStats) {
            const arenaLabel = document.createElement('label');
            arenaLabel.className = 'toggle-label';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.checked = true;

            checkbox.addEventListener('change', (e) => {
                includeArena = e.target.checked;
                arenaOnlyTroopLabels.forEach(lbl => {
                    lbl.style.display = includeArena ? '' : 'none';
                });
                renderTable();
            });

            const span = document.createElement('span');
            span.textContent = 'vs Arena';

            arenaLabel.appendChild(checkbox);
            arenaLabel.appendChild(span);
            togglesList.appendChild(arenaLabel);
        }

        if (showTroop) {
            classesPresent.forEach(cls => {
                const troopLabel = document.createElement('label');
                troopLabel.className = 'toggle-label';

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.checked = true;

                checkbox.addEventListener('change', (e) => {
                    if (e.target.checked) activeClasses.add(cls);
                    else activeClasses.delete(cls);
                    renderTable();
                });

                const span = document.createElement('span');
                span.textContent = `${cls} Stats`;

                troopLabel.appendChild(checkbox);
                troopLabel.appendChild(span);
                togglesList.appendChild(troopLabel);

                if (hasArenaStats && !hasNonArenaClassStat(cls)) {
                    arenaOnlyTroopLabels.push(troopLabel);
                }
            });
        }

        if (togglesList.children.length > 0) {
            wrapper.insertBefore(toggleWrapper, table);
        }
    }

    return wrapper;
}

/**
 * Merges two scenario data objects into one, summing up their statistics.
 * @param {Object} target - The existing accumulated totals (null for first call)
 * @param {Object} source - The new slot data to add
 * @returns {Object} The merged/accumulated data
 */
function mergeScenarioStats(target, source, class1, class2) {
    if (!source) return target;
    if (!target) {
        target = {
            set1: { stacked: {}, health: {}, multipliers: {}, meta: {} },
            set2: { stacked: {}, health: {}, multipliers: {}, meta: {} },
            diffs: {}
        };
    }

    const mergeWithClass = (t, s, cls) => {
        if (!s) return;
        for (const [k, v] of Object.entries(s)) {
            let key = k;
            if (classSpecificStats.includes(k)) {
                const targetCls = getStatTargetClass(k, cls);
                let suffix = targetCls ? targetCls : 'None';
                key = `${k}|${suffix}`;
            }
            t[key] = (t[key] || 0) + v;
        }
    };

    mergeWithClass(target.set1.stacked, source.set1.stacked, class1);
    mergeWithClass(target.set2.stacked, source.set2.stacked, class2);

    if (source.set1.health) {
        target.set1.health = target.set1.health || {};
        mergeWithClass(target.set1.health, source.set1.health, class1);
    }
    if (source.set2.health) {
        target.set2.health = target.set2.health || {};
        mergeWithClass(target.set2.health, source.set2.health, class2);
    }

    if (source.set1.multipliers) {
        target.set1.multipliers = target.set1.multipliers || {};
        mergeWithClass(target.set1.multipliers, source.set1.multipliers, class1);
    }
    if (source.set2.multipliers) {
        target.set2.multipliers = target.set2.multipliers || {};
        mergeWithClass(target.set2.multipliers, source.set2.multipliers, class2);
    }

    // Merge meta stats natively without class tagging
    if (source.set1.meta) {
        for (const [k, v] of Object.entries(source.set1.meta)) {
            if (typeof v === 'number') target.set1.meta[k] = (target.set1.meta[k] || 0) + v;
            else if (typeof v === 'boolean') target.set1.meta[k] = target.set1.meta[k] || v;
        }
    }
    if (source.set2.meta) {
        for (const [k, v] of Object.entries(source.set2.meta)) {
            if (typeof v === 'number') target.set2.meta[k] = (target.set2.meta[k] || 0) + v;
            else if (typeof v === 'boolean') target.set2.meta[k] = target.set2.meta[k] || v;
        }
    }

    return target;
}

/**
 * Creates a kebab menu (three-dot) dropdown wrapper for slot-level actions.
 * @param {string} slotName - Name of the gear slot or 'totals'
 * @returns {HTMLElement} The dropdown menu wrapper element
 */
function createKebabDropdown(slotName) {
    const dropdownWrapper = document.createElement('div');
    dropdownWrapper.className = 'dropdown-menu-wrapper';

    const kebabBtn = document.createElement('button');
    kebabBtn.type = 'button';
    kebabBtn.className = 'icon-only kebab-btn';
    kebabBtn.setAttribute('aria-label', `${slotName} Actions`);
    kebabBtn.setAttribute('title', 'Actions');
    kebabBtn.setAttribute('aria-expanded', 'false');
    kebabBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="5" cy="12" r="1.5"></circle>
            <circle cx="12" cy="12" r="1.5"></circle>
            <circle cx="19" cy="12" r="1.5"></circle>
        </svg>
    `;

    const dropdownMenu = document.createElement('ul');
    dropdownMenu.className = 'dropdown-menu';

    const shareLi = document.createElement('li');
    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.className = 'dropdown-item';
    shareBtn.innerHTML = `
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
            <polyline points="16 6 12 2 8 6"></polyline>
            <line x1="12" y1="2" x2="12" y2="15"></line>
        </svg>
        Share
    `;
    shareBtn.onclick = (e) => {
        e.stopPropagation();
        dropdownMenu.classList.remove('show');
        kebabBtn.setAttribute('aria-expanded', 'false');
        exportSlotToDiscord(slotName);
    };
    shareLi.appendChild(shareBtn);
    dropdownMenu.appendChild(shareLi);

    const downloadLi = document.createElement('li');
    const downloadBtn = document.createElement('button');
    downloadBtn.type = 'button';
    downloadBtn.className = 'dropdown-item';
    downloadBtn.innerHTML = `
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        Download
    `;
    downloadBtn.onclick = (e) => {
        e.stopPropagation();
        dropdownMenu.classList.remove('show');
        kebabBtn.setAttribute('aria-expanded', 'false');
        exportSlotToCSV(slotName);
    };
    downloadLi.appendChild(downloadBtn);
    dropdownMenu.appendChild(downloadLi);

    // Toggle dropdown on kebab button click
    kebabBtn.onclick = (e) => {
        e.stopPropagation();
        const wasExpanded = kebabBtn.getAttribute('aria-expanded') === 'true';
        
        // Close all other dropdowns first
        document.querySelectorAll('.dropdown-menu.show').forEach(menu => {
            menu.classList.remove('show');
            menu.previousElementSibling?.setAttribute('aria-expanded', 'false');
        });

        if (!wasExpanded) {
            dropdownMenu.classList.add('show');
            kebabBtn.setAttribute('aria-expanded', 'true');
        } else {
            dropdownMenu.classList.remove('show');
            kebabBtn.setAttribute('aria-expanded', 'false');
        }
    };

    dropdownWrapper.appendChild(kebabBtn);
    dropdownWrapper.appendChild(dropdownMenu);
    return dropdownWrapper;
}

